import styled from '@emotion/styled';
import {
  spacing,
  mediaMax,
  fontSize,
  fontWeight,
} from 'presentations/views/styles';
import BlogPageTemplate from 'presentations/views/components/templates/BlogPageTemplate';
import BlogList, {
  Props as BlogListProps,
} from 'presentations/views/components/organisms/BlogList';
import FormContentHeader from 'presentations/views/components/molecules/FormContentHeader';
import { useBlogListPage } from './hooks';
import Breadcrumbs from 'presentations/views/components/organisms/Breadcrumbs';
import { ReactNode } from 'react';
import Icon from 'presentations/views/components/atoms/Icon';
import { primary } from 'presentations/views/styles/color';

export interface Props {
  title: string;
  tag: string | null;
  category: string | null;
  blogListProps: BlogListProps;
  breadcrumbs: { title: string; link: string }[];
  siderbar?: ReactNode;
  header?: ReactNode;
}

export default function Component(props: Props) {
  const { state, actions } = useBlogListPage({
    tag: props.tag,
    category: props.category,
    blogs: props.blogListProps.items,
    breadcrumbs: props.breadcrumbs,
  });
  return (
    <BlogPageTemplate
      breadcrumbs={<Breadcrumbs routes={state.routes} />}
      header={props.header}
      contentHeader={
        props.tag ? (
          <FormContentHeader
            icon={props.tag ? 'hashtag' : 'note-text-outline'}
            title={props.title}
          />
        ) : null
      }
    >
      <CategoryNameContainer>
        <CategoryIcon name="note-text-outline" />
        <CategoryTitle>{props.title}</CategoryTitle>
      </CategoryNameContainer>
      <Container>
        <BlogListContainer>
          <BlogList title={props.title} {...props.blogListProps} />
        </BlogListContainer>
        {props.siderbar && (
          <SidebarContainer>{props.siderbar}</SidebarContainer>
        )}
      </Container>
    </BlogPageTemplate>
  );
}

const Container = styled.div`
  max-width: 960px;
  margin: ${spacing(64)} auto ${spacing(80)};
  display: flex;
  flex-direction: row;

  ${mediaMax.desktop} {
    padding: 0 ${spacing(24)};
  }
  ${mediaMax.tablet} {
    flex-direction: column;
    margin: ${spacing(24)} 0;
  }
  ${mediaMax.xs} {
    width: 100%;
  }
`;

const BlogListContainer = styled.div`
  padding: 0 ${spacing(48)} ${spacing(72)} 0;

  ${mediaMax.tablet} {
    padding: 0;
  }
`;

const SidebarContainer = styled.div`
  width: 274px;
  min-width: 274px;

  ${mediaMax.tablet} {
    margin: ${spacing(32)} 0 0 0;
    width: inherit;
    min-width: inherit;
  }
`;

const CategoryNameContainer = styled.div`
  display: flex;
  align-items: center;
  max-width: 960px;
  margin: ${spacing(40)} auto;
  ${mediaMax.tablet} {
    margin: ${spacing(24)} auto;
    padding: 0 ${spacing(12)};
  }
`;
const CategoryIcon = styled(Icon)`
  color: ${primary};
  margin-right: ${spacing(8)};
  width: 32px !important;
  height: 32px !important;
  ${mediaMax.tablet} {
    width: 24px !important;
    height: 24px !important;
  }
`;
const CategoryTitle = styled.div`
  font-size: ${fontSize(32)};
  font-weight: ${fontWeight('bold')};
  ${mediaMax.tablet} {
    font-size: ${fontSize(24)};
  }
`;
